// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Governance: effective policies and pending human approvals.
 *
 * This is the one control-plane screen that submits a state change, so the
 * relevant controls are concentrated here:
 *
 * - the reviewer sees the exact action, target, material inputs, side effects,
 *   and evidence before deciding (`ARCH-026`, `BUS-005`), rendered by the
 *   shared `ApprovalPanel`;
 * - the decision carries the payload digest the reviewer actually saw, so a
 *   changed payload is rejected deterministically by the service (`ARCH-026`);
 * - the approve control is offered only when the verified session carries the
 *   approval capability, and that is an affordance — the service authorises
 *   every decision independently (`CST-048`, `GOV-050`); and
 * - a failed decision surfaces the service's own problem document; the UI never
 *   retries a submitted decision, because its outcome is not known to be
 *   un-applied (`RUN-010`).
 */

import {
  ApiProblemError,
  AsyncSection,
  capabilities,
  DataTable,
  ErrorState,
  hasCapability,
  PageHeader,
  queryKeys,
  RequireCapability,
  StateBadge,
  useApiClients,
  useSession,
  type ApprovalRequest,
  type PolicyView,
} from '@eai/ui-shared';
import { ApprovalPanel } from '@eai/ui-shared';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { useControlPlaneQuery } from '../app-shell';

const POLICY_EFFECT_TONE = {
  allow: 'positive',
  deny: 'negative',
  not_applicable: 'neutral',
  indeterminate: 'caution',
} as const;

/** Governance screen: policies and approvals. */
export function GovernanceScreen(): ReactNode {
  const { session, scope } = useSession();
  const clients = useApiClients();
  const queryClient = useQueryClient();
  const [decisionError, setDecisionError] = useState<ApiProblemError | null>(null);

  const policies = useControlPlaneQuery(
    (cacheScope) => queryKeys.controlPlane.policies(cacheScope),
    (client, signal) => client.listPolicies({ signal }),
  );
  const approvals = useControlPlaneQuery(
    (cacheScope) => queryKeys.controlPlane.approvals(cacheScope),
    (client, signal) => client.listApprovals({ signal }),
  );

  const canApprove = hasCapability(session, capabilities.governanceApprove);

  const decide = useMutation({
    // A submitted decision has an unknown outcome until the service answers,
    // so it is never retried automatically (`RUN-010`).
    retry: false,
    mutationFn: (input: {
      approvalId: string;
      decision: 'approve' | 'reject';
      payloadDigest: string;
      justification: string;
    }) => clients.controlPlane.submitApprovalDecision(input),
    onSuccess: () => {
      setDecisionError(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.controlPlane.approvals(scope) });
    },
    onError: (error: unknown) => {
      setDecisionError(
        error instanceof ApiProblemError
          ? error
          : new ApiProblemError('The decision could not be recorded.', {
              kind: 'server',
              status: 0,
              cause: error,
            }),
      );
    },
  });

  return (
    <RequireCapability required={[capabilities.governanceRead]} area="Governance">
      <PageHeader
        title="Governance"
        description="Effective policies and the high-impact actions awaiting a human decision."
        {...(session?.university_label === null || session?.university_label === undefined
          ? {}
          : { scopeLabel: session.university_label })}
      />

      <Typography variant="h2" component="h2" gutterBottom>
        Policies
      </Typography>
      <AsyncSection
        query={policies}
        loadingLabel="Loading policies"
        emptyTitle="No policy is published"
        emptyDescription="Publish a policy through the governance workflow before it can take effect."
        isEmpty={(data) => data.items.length === 0}
      >
        {(data) => (
          <DataTable<PolicyView>
            caption="Effective policies"
            rows={data.items}
            rowKey={(row) => row.policy_id}
            columns={[
              { key: 'name', header: 'Policy', render: (row) => row.name },
              { key: 'version', header: 'Version', render: (row) => row.version },
              { key: 'scope', header: 'Scope', render: (row) => row.scope_label },
              {
                key: 'effect',
                header: 'Effect',
                render: (row) => (
                  <StateBadge
                    label={row.effect}
                    tone={POLICY_EFFECT_TONE[row.effect]}
                    subject={`${row.name} effect`}
                  />
                ),
              },
              {
                key: 'enabled',
                header: 'State',
                render: (row) => (
                  <StateBadge
                    label={row.enabled ? 'Enabled' : 'Disabled'}
                    tone={row.enabled ? 'positive' : 'neutral'}
                    subject={`${row.name} state`}
                  />
                ),
              },
              {
                key: 'updated',
                header: 'Updated',
                render: (row) => row.updated_at ?? 'Not reported',
              },
            ]}
          />
        )}
      </AsyncSection>

      <Typography variant="h2" component="h2" sx={{ mt: 4 }} gutterBottom>
        Pending approvals
      </Typography>

      {canApprove ? null : (
        <Alert severity="info" role="note" sx={{ mb: 2 }}>
          Your account was not offered the approval capability, so decisions are shown read-only.
          The service authorises every decision independently.
        </Alert>
      )}

      {decisionError === null ? null : <ErrorState error={decisionError} />}

      <AsyncSection
        query={approvals}
        loadingLabel="Loading approvals"
        emptyTitle="No approval is pending"
        emptyDescription="High-impact actions appear here when a service requests a human decision."
        isEmpty={(data) => data.items.length === 0}
      >
        {(data) => (
          <Stack spacing={3}>
            {data.items.map((approval: ApprovalRequest) => (
              <ApprovalPanel
                key={approval.approval_id}
                approval={approval}
                canDecide={canApprove}
                submitting={decide.isPending}
                onDecide={(input) => {
                  decide.mutate({ approvalId: approval.approval_id, ...input });
                }}
              />
            ))}
          </Stack>
        )}
      </AsyncSection>
    </RequireCapability>
  );
}
